<template>
    <div class="mx-3">
        <advanced-claim-search-modal :search="advancedSearch"></advanced-claim-search-modal>

        <div v-if="historical" class="alert alert-primary text-center" role="alert">
            <h5><i class="fas fa-history"></i> Historical</h5>       
        </div>        

        <h4>{{ client.name }}</h4>
        <div class="d-flex justify-content-between align-items-end">
            <h2>{{ title }}</h2>
            <div class="btn-toolbar mb-2" role="toolbar">
                <div v-if="state.searchOption" class="input-group mr-2">
                    <div class="input-group-prepend">
                        <button class="btn dropdown-toggle input-group-text" type="button" data-toggle="dropdown">{{ state.searchOption.name }}</button>
                        <div class="dropdown-menu">
                            <button v-for="searchOption in searchOptions" :key="'search-option-' + searchOption.id" class="dropdown-item text-primary" @click="state.searchOption = searchOption">{{ searchOption.name }}</button>
                        </div>
                    </div>                    
                    <input ref="searchInput" type="text" class="form-control" :placeholder="state.searchOption.placeholder" v-model="state.searchString" @keyup.enter="search()">
                    <div class="input-group-append">
                        <button class="btn btn-primary" @click="search()"><i class="fas fa-search fa-lg"></i></button>
                    </div>                    
                </div>                    
                <div class="btn-group">
                    <a href="#" class="btn btn-primary" data-toggle="tooltip" title="Advanced Search" @click="showAdvancedSearch()">
                        <i class="fas fa-search-plus fa-lg"></i>                   
                    </a>
                </div>
            </div>
        </div>

        <div v-if="claims.length" class="table-responsive">
            <table class="table table-striped table-sm">
                <thead>
                    <tr>
                        <th></th>
                        <th class="fit">Claim Number</th>
                        <th>Patient Name</th> 
                        <th>Date of Birth</th>
                        <th>Date of Injury</th>                                     
                        <th>Provider</th>
                        <th>Payer</th>
                        <th></th>
                    </tr>                    
                </thead>
                <tbody>
                    <tr v-for="claim in claims" :key="getKey(claim.id)">
                        <td class="fit">
                            <div class="d-flex align-items-center">
                                <i :class="['fas fa-fw fa-sm text-muted mx-1', {'fa-balance-scale': claim.shared}]" :title="claim.shared ? 'Shared with Attorney' : false"></i>                            
                                <i :class="['fas fa-fw fa-lg text-warning', {'fa-exclamation-triangle': claim.status === 'Attention'}]" :title="claim.status"></i>
                            </div>
                        </td>
                        <td class="fit"><a :href="getClaimUrl(claim)" v-text="claim.claim_number"></a></td>                         
                        <td><a :href="getClaimUrl(claim)" v-text="claim.patient_name"></a></td>
                        <td>{{ claim.patient_dob }}</td>
                        <td>{{ claim.injury_date }}</td>
                        <td>{{ claim.provider_name }}</td>
                        <td>{{ claim.payer_name }}</td>                      
                        <td>
                            <div class="d-flex flex-nowrap flex-row justify-content-end">
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>

            <div class="d-flex">
                <nav v-if="pageCount > 1" aria-label="Page navigation">
                    <ul class="pagination">
                        <li v-bind:class="['page-item', { 'disabled' : state.currentPage == 1 }]">
                            <a class="page-link" href="#" @click="first()">
                                <span id="first-page"><i class="fas fa-step-backward fa-xs chev-pos"></i></span>
                            </a>
                        </li>                    
                        <li v-bind:class="['page-item', { 'disabled' : state.currentPage == 1 }]">
                            <a class="page-link" href="#" @click="previous()">
                                <span id="previous"><i class="fas fa-chevron-left fa-xs chev-pos"></i><i class="fas fa-chevron-left fa-xs mr-1 chev-pos"></i>Previous</span>
                            </a>
                        </li>
                        <li v-bind:class="['page-item', { 'disabled' : state.currentPage == pageCount }]">
                            <a class="page-link" href="#" @click="next()">
                                <span id="next">Next<i class="fas fa-chevron-right fa-xs ml-2 chev-pos"></i><i class="fas fa-chevron-right fa-xs chev-pos"></i></span>
                            </a>
                        </li>
                        <li v-bind:class="['page-item', { 'disabled' : state.currentPage == pageCount }]">
                            <a class="page-link" href="#" @click="last()">
                                <span id="first-page"><i class="fas fa-step-forward fa-xs chev-pos"></i></span>
                            </a>
                        </li>                    
                    </ul>
                </nav>
                <div class="ml-auto"> Page {{ state.currentPage }} of {{ pageCount }}</div>
            </div>
        </div>
        <div v-else-if="loading" class="pt-3 border-top">            
            <h3 class="text-muted text-center"><i class="fas fa-spinner fa-lg fa-spin align-middle"></i> Loading...</h3>
        </div>
        <div v-else class="pt-3 border-top">
            <h3 class="text-muted text-center">No Claims</h3>
        </div>

    </div>   
</template>

<script>
import AdvancedClaimSearchModal from '../AdvancedClaimSearchComponent.vue';

export default {
    components: {
        AdvancedClaimSearchModal
    },
    props: {
        client: Object,
        title: String,
        href: String,
        searchOptions: Array,
        selectedSearchOptionId: Number,
        searchText: String,
        page: Number,
        historical: Boolean
    },
    data() {
        return {
            state: {
                searchString: this.searchText,
                currentPage: this.page,
                searchOption: this.searchOptions.find(x => x.id === this.selectedSearchOptionId)
            },
            advancedCriteria: null,
            pageCount: 0,
            claims: [],
            loading: true,
        }
    },
    methods: {
        refresh: function () {

            this.loading = true;

            axios.get(this.href, {
                params: {
                    'search_option_id': this.state.searchOption.id,
                    'search_string': this.state.searchString,
                    'page': this.state.currentPage - 1,
                    'advanced_criteria': this.advancedCriteria
                }
            })
            .then(response => {

                this.claims = response.data.claims;
                this.pageCount = response.data.page_count;
                this.loading = false;

                // reset advances search criteria
                this.advancedCriteria = null;
            })
            .catch(error => {

                console.log(error);
                this.loading = false;
            });
        },
        search: function () {
            this.state.currentPage = 1;
            this.refresh();
        },
        showAdvancedSearch: function () {

            // clear simple search criteria
            this.state.searchString = '';
            eventHub.$emit('show-advanced-claim-search', this.historical);
        },
        advancedSearch: function (criteria) {

            this.advancedCriteria = Object.values(criteria).every(x => x === null) ? null : criteria;
            this.state.currentPage = 1;
            this.refresh();
        },
        getKey: function (id) {
            let key = 'researcher-claim-list-' + id;
            return key;
        },  
        getClaimUrl: function (claim) {

            let url = new URL(window.location.origin + '/researcher/clients/' + this.client.id + '/claims/' + claim.id);
            url.searchParams.append('clientId', this.client.id);
            url.searchParams.append('searchOptionId', this.state.searchOption.id);
            url.searchParams.append('searchString', this.state.searchString);
            url.searchParams.append('page', this.state.currentPage);

            return url.href;            
        },
        first: function () {
            if (this.state.currentPage > 1) {
                this.state.currentPage = 1;
                this.refresh();
            }
        },        
        previous: function() {
            if (this.state.currentPage > 0) {
                this.state.currentPage--;
                this.refresh();
            }
        },
        goto: function(page) {
            this.state.currentPage = page;
            this.refresh();
        },
        next: function() {
            if (this.state.currentPage < this.pageCount) {
                this.state.currentPage++;
                this.refresh();
            }
        },
        last: function () {
            if (this.state.currentPage < this.pageCount) {
                this.state.currentPage = this.pageCount;
                this.refresh();
            }
        }        
    },
    created() {
        this.refresh();

        // if we get a claim event update the list
        const self = this;
        window.Echo.private('claim-event.' + this.client.id).listen('ClaimEvent', (e) => {
            self.refresh();
        });
    },
    beforeDestroy() {

        // leave all channel
        window.Echo.leave('claim-event.' + this.client.id);

    },
    mounted() {        
        this.$refs.searchInput.setAttribute('size', this.$refs.searchInput.getAttribute('placeholder').length);
    }    
}
</script>

<style scoped>

</style>